import { Controller } from "@hotwired/stimulus"

// Drag/drop multi-upload modal for Admin Documents index.
//
// Strategy:
// - Listen for drag events on the page.
// - On drop, build a row per File with:
//   - title (prefilled from filename)
//   - category select
//   - optional meeting_date (shown when meeting minutes selected)
//   - hidden file input whose FileList is set via DataTransfer
// - Submit as params: documents[][title], documents[][document_category_id], documents[][visibility], documents[][meeting_date], documents[][file]

export default class extends Controller {
  static values = {
    categories: Array,
    bulkCreateUrl: String,
  }

  static targets = [
    "modal",
    "backdrop",
    "rows",
    "form",
  ]

  connect() {
    this._dragCounter = 0

    this._onDragEnter = (e) => {
      if (this._hasFiles(e)) {
        this._dragCounter++
        this._showDropHint()
      }
    }

    this._onDragLeave = (e) => {
      if (this._hasFiles(e)) {
        this._dragCounter--
        if (this._dragCounter <= 0) this._hideDropHint()
      }
    }

    this._onDragOver = (e) => {
      if (this._hasFiles(e)) {
        e.preventDefault()
        e.dataTransfer.dropEffect = "copy"
      }
    }

    this._onDrop = (e) => {
      if (!this._hasFiles(e)) return
      e.preventDefault()
      this._dragCounter = 0
      this._hideDropHint()

      const files = Array.from(e.dataTransfer.files || [])
      if (files.length === 0) return

      this.openWithFiles(files)
    }

    document.addEventListener("dragenter", this._onDragEnter)
    document.addEventListener("dragleave", this._onDragLeave)
    document.addEventListener("dragover", this._onDragOver)
    document.addEventListener("drop", this._onDrop)
  }

  disconnect() {
    document.removeEventListener("dragenter", this._onDragEnter)
    document.removeEventListener("dragleave", this._onDragLeave)
    document.removeEventListener("dragover", this._onDragOver)
    document.removeEventListener("drop", this._onDrop)
  }

  openWithFiles(files) {
    // Clear previous
    this.rowsTarget.innerHTML = ""

    // Ensure form action
    if (this.hasBulkCreateUrlValue) {
      this.formTarget.action = this.bulkCreateUrlValue
    }

    files.forEach((file) => this._appendRowForFile(file))

    this.modalTarget.classList.remove("hidden")
    this.backdropTarget.classList.remove("hidden")
  }

  close() {
    this.modalTarget.classList.add("hidden")
    this.backdropTarget.classList.add("hidden")
    this.rowsTarget.innerHTML = ""
  }

  _appendRowForFile(file) {
    const row = document.createElement("div")
    row.className = "grid grid-cols-12 gap-3 items-end"

    // Title
    const titleWrap = document.createElement("div")
    titleWrap.className = "col-span-12 md:col-span-6"
    titleWrap.innerHTML = `
      <label class="block text-sm font-medium text-gray-700">Title</label>
      <input type="text" name="documents[][title]" value="${this._escape(this._defaultTitle(file.name))}" class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
    `

    // Category
    const categoryWrap = document.createElement("div")
    categoryWrap.className = "col-span-12 md:col-span-4"

    const options = this.categoriesValue
      .map((c) => `<option value="${this._escape(String(c.id))}" data-category-name="${this._escape(c.name || "")}">${this._escape(c.display_name)}</option>`)
      .join("")

    categoryWrap.innerHTML = `
      <label class="block text-sm font-medium text-gray-700">Category</label>
      <select name="documents[][document_category_id]" class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
        ${options}
      </select>
    `

    const categorySelect = categoryWrap.querySelector("select")

    // Meeting date (hidden unless meeting_minutes)
    const dateWrap = document.createElement("div")
    dateWrap.className = "col-span-12 md:col-span-2 hidden"
    dateWrap.innerHTML = `
      <label class="block text-sm font-medium text-gray-700">Meeting date</label>
      <input type="date" name="documents[][meeting_date]" class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
    `

    const dateInput = dateWrap.querySelector("input")

    // Hidden visibility
    const visibility = document.createElement("input")
    visibility.type = "hidden"
    visibility.name = "documents[][visibility]"
    visibility.value = "visible"

    // Hidden file input
    const fileInput = document.createElement("input")
    fileInput.type = "file"
    fileInput.name = "documents[][file]"
    fileInput.className = "hidden"

    const dt = new DataTransfer()
    dt.items.add(file)
    fileInput.files = dt.files

    // Meeting minutes toggle
    const updateMeetingDateVisibility = () => {
      const selected = categorySelect.options[categorySelect.selectedIndex]
      const name = selected?.dataset?.categoryName
      if (name === "meeting_minutes") {
        dateWrap.classList.remove("hidden")
        dateInput.required = true
      } else {
        dateWrap.classList.add("hidden")
        dateInput.required = false
        dateInput.value = ""
      }
    }

    categorySelect.addEventListener("change", updateMeetingDateVisibility)
    updateMeetingDateVisibility()

    // Resource center checkbox
    const resourceCenterWrap = document.createElement("div")
    resourceCenterWrap.className = "col-span-12 flex items-center gap-2"
    const resourceCenterCheckbox = document.createElement("input")
    resourceCenterCheckbox.type = "checkbox"
    resourceCenterCheckbox.name = "documents[][resource_center]"
    resourceCenterCheckbox.value = "1"
    resourceCenterCheckbox.id = `resource_center_${Math.random().toString(36).slice(2)}`
    resourceCenterCheckbox.className = "h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
    const resourceCenterLabel = document.createElement("label")
    resourceCenterLabel.htmlFor = resourceCenterCheckbox.id
    resourceCenterLabel.className = "text-sm text-gray-700"
    resourceCenterLabel.textContent = "Show in Resource Center"
    resourceCenterWrap.appendChild(resourceCenterCheckbox)
    resourceCenterWrap.appendChild(resourceCenterLabel)

    row.appendChild(titleWrap)
    row.appendChild(categoryWrap)
    row.appendChild(dateWrap)
    row.appendChild(resourceCenterWrap)
    row.appendChild(visibility)
    row.appendChild(fileInput)

    // File name helper row
    const fileName = document.createElement("div")
    fileName.className = "col-span-12 text-xs text-gray-500"
    fileName.textContent = `File: ${file.name}`

    this.rowsTarget.appendChild(row)
    this.rowsTarget.appendChild(fileName)
    this.rowsTarget.appendChild(document.createElement("hr")).className = "my-3"
  }

  _defaultTitle(filename) {
    // Strip final extension
    return filename.replace(/\.[^.]+$/, "")
  }

  _hasFiles(e) {
    const types = e.dataTransfer?.types
    return types && Array.from(types).includes("Files")
  }

  _showDropHint() {
    this.element.classList.add("ring-2", "ring-blue-400", "ring-offset-2")
  }

  _hideDropHint() {
    this.element.classList.remove("ring-2", "ring-blue-400", "ring-offset-2")
  }

  _escape(str) {
    return String(str)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }
}
